import React from "react";
import appScreenshots from "../../assets/AppScreenshots.png";
import appScreenshotsMobile from "../../assets/appscreenshots_mobile.png";
import cafeImg from "../../assets/cafe.png";
import GlowButton from "./GlowButton";
import DatapointBadges from "./DatapointBadges";
import increaseRevenueIcon from "../../assets/increase_revenue.png";
import reduceCostIcon from "../../assets/reduce_cost.png";
import noChargeIcon from "../../assets/no_charge.png";
import fullAccessIcon from "../../assets/full_access.png";
import improveRetentionIcon from "../../assets/improve_retention.png";
import setupIn5Icon from "../../assets/setup_in_5.png";

const steps = [
  {
    number: "1",
    title: "create your profile",
    description: "add your business details, logo, and content in just 2 minutes.",
  },
  {
    number: "2",
    title: "set up your tools",
    description: "add team members, add menu/catalogs with ai and enable ai in minutes.",
  },
  {
    number: "3",
    title: "improve customer service",
    description: "go live and watch your customer retention increase immediately.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      className="w-full bg-white text-black py-6 md:py-20 px-4 sm:px-6 md:px-12 lg:px-16 overflow-x-hidden"
    >
      <div className="max-w-[1260px] mx-auto">

        {/* Heading */}
        <div className="relative flex items-center justify-center mb-12 md:mb-16">
          <div className="text-center">
            <p className="text-2xl sm:text-[40px] md:text-4xl lg:text-[50px] font-bold text-[#FCBA05] mb-3">
              how it works
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-bold font-lexend leading-tight text-black">
              setup is easier than brewing a cup of coffee
            </h2>
          </div>
          <img
            src={cafeImg}
            alt="coffee cup"
            className="absolute right-0 top-0 w-32 lg:w-40 object-contain pointer-events-none hidden md:block"
          />
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mb-16 md:mb-20">
          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center text-center gap-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#6E3FC6] text-white text-2xl font-bold shadow-lg">
                {step.number}
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-lexend text-black">
                {step.title}
              </h3>
              <p className="text-gray-500 text-sm sm:text-base font-poppins leading-relaxed max-w-xs">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* MOBILE: app screenshots with badges below */}
        <div className="md:hidden flex flex-col w-full gap-8">
          <div className="flex flex-col gap-3 w-full">
            {[
              { icon: reduceCostIcon,       text: "reduce cost by 30%",          style: { backgroundColor: "#FFC721" } },
              { icon: noChargeIcon,         text: "no charge first 30 days",     style: { backgroundColor: "rgba(100,136,242,0.85)", backdropFilter: "blur(10.2px)" } },
              { icon: fullAccessIcon,       text: "full access to all features", style: { backgroundColor: "#F79669" } },
              { icon: increaseRevenueIcon,  text: "increase revenue by 3%-5%",   style: { backgroundColor: "#E83DE6" } },
              { icon: setupIn5Icon,         text: "setup in under 5 minutes",    style: { backgroundColor: "#DF60FF" } },
              { icon: improveRetentionIcon, text: "improve customer retention",  style: { backgroundColor: "#FFA850" } },
            ].map((b) => (
              <div
                key={b.text}
                className="flex items-center gap-3 text-white text-md font-medium px-4 py-3 rounded-full whitespace-nowrap w-full overflow-visible"
                style={b.style}
              >
                <img src={b.icon} alt={b.text} className="w-7 h-7 object-contain shrink-0 my-[-4px]" />
                <span>{b.text}</span>
              </div>
            ))}
          </div>
          <img
            src={appScreenshotsMobile}
            alt="app screenshots"
            className="w-full object-contain scale-110"
          />
        </div>

        {/* DESKTOP: app screenshots with overlaid badges */}
        <div className="hidden md:block relative w-full max-w-[1200px] mx-auto">
          <img
            src={appScreenshots}
            alt="app screenshots"
            className="w-full object-contain"
          />
          {/* Left badges — individually positioned */}
          {[
            { icon: noChargeIcon,         text: "no charge first 30 days",    top: "10%", marginLeft: "90px" },
            { icon: fullAccessIcon,       text: "full access to all features", top: "38%", marginLeft: "62px" },
            { icon: improveRetentionIcon, text: "improve customer retention",  top: "63%", marginLeft: "58px" },
          ].map((b) => (
            <div
              key={b.text}
              className="absolute left-0 flex items-center gap-2 bg-[#FF9114] text-white text-sm font-medium px-4 py-2.5 rounded-full whitespace-nowrap shadow-md pointer-events-none overflow-visible"
              style={{ top: b.top, marginLeft: b.marginLeft, transform: "translateY(-50%)" }}
            >
              <span>{b.text}</span>
              <img src={b.icon} alt={b.text} className="w-7 h-7 object-contain shrink-0 my-[-4px]" />
            </div>
          ))}
          {/* Right badges — individually positioned */}
          {[
            { icon: increaseRevenueIcon, text: "increase revenue by 3%-5%", top: "10%", marginRight: "68px" },
            { icon: reduceCostIcon,      text: "reduce cost by 30%",        top: "32%", marginRight: "80px" },
            { icon: setupIn5Icon,        text: "setup in under 5 minutes",  top: "64%", marginRight: "70px" },
          ].map((b) => (
            <div
              key={b.text}
              className="absolute right-0 flex items-center gap-2 bg-[#FF9114] text-white text-sm font-medium px-4 py-2.5 rounded-full whitespace-nowrap shadow-md pointer-events-none overflow-visible"
              style={{ top: b.top, marginRight: b.marginRight, transform: "translateY(-50%)" }}
            >
              <img src={b.icon} alt={b.text} className="w-7 h-7 object-contain shrink-0 my-[-4px]" />
              <span>{b.text}</span>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mt-4 md:mt-0">
          <button
            onClick={() => window.location.href = "/business/demo"}
            className="inline-flex items-center justify-center whitespace-nowrap border border-[#6E3FC6] bg-[#e9d9ff] hover:bg-[#d8bfff] text-black text-sm sm:text-base font-medium px-[18px] sm:px-[25px] lg:px-[40px] rounded-full transition-all duration-300 cursor-pointer"
          >
            how it works
          </button>
          <GlowButton
            btnValue="free trial"
            btn1="2"
            onClick={() => window.location.href = "/business/get-started"}
          />
        </div>

      </div>
    </section>
  );
};

export default HowItWorks;
